import { z } from 'zod';
import type { FieldError } from '../errors';
import { isoTimestamp } from './common';

export const PROVIDER_TYPES = [
  'anthropic',
  'openai',
  'google',
  'openai_compatible',
  'claude_agent_sdk',
] as const;
export const providerTypeSchema = z.enum(PROVIDER_TYPES);
export type ProviderType = z.infer<typeof providerTypeSchema>;

/** How a provider caches prompts: we mark breakpoints, it does it itself, or it is up to the server. */
export type PromptCacheMode = 'explicit' | 'automatic' | 'server' | 'managed';
export const PROMPT_CACHE_MODES: Record<ProviderType, { mode: PromptCacheMode; note: string }> = {
  anthropic: {
    mode: 'explicit',
    note: 'Cache breakpoints on the system prompt and history; prompts under ~1k tokens are not cached.',
  },
  openai: {
    mode: 'automatic',
    note: 'Automatic for identical prefixes of 1,024+ tokens; we send a per-agent cache key.',
  },
  google: {
    mode: 'automatic',
    note: 'Implicit caching for identical prefixes above the model minimum (about 1k–2k tokens); best effort.',
  },
  openai_compatible: { mode: 'server', note: 'Depends on the server behind the base URL.' },
  claude_agent_sdk: { mode: 'managed', note: 'The Claude Agent SDK manages caching itself.' },
};

export interface ProviderTypeInfo {
  label: string;
  description: string;
  apiKey: 'required' | 'optional';
  baseUrl: 'none' | 'optional' | 'required';
  defaultBaseUrl: string | null;
  baseUrlPlaceholder: string | null;
  apiKeyPlaceholder: string;
}

export const PROVIDER_CATALOG: Record<ProviderType, ProviderTypeInfo> = {
  anthropic: {
    label: 'Anthropic',
    description: 'Claude models through the Anthropic API.',
    apiKey: 'required',
    baseUrl: 'optional',
    defaultBaseUrl: 'https://api.anthropic.com',
    baseUrlPlaceholder: 'https://api.anthropic.com',
    apiKeyPlaceholder: 'sk-ant-…',
  },
  openai: {
    label: 'OpenAI',
    description: 'GPT and o-series models through the OpenAI API.',
    apiKey: 'required',
    baseUrl: 'optional',
    defaultBaseUrl: 'https://api.openai.com/v1',
    baseUrlPlaceholder: 'https://api.openai.com/v1',
    apiKeyPlaceholder: 'sk-…',
  },
  google: {
    label: 'Google Gemini',
    description: 'Gemini models through the Gemini API.',
    apiKey: 'required',
    baseUrl: 'none',
    defaultBaseUrl: 'https://generativelanguage.googleapis.com/v1beta',
    baseUrlPlaceholder: null,
    apiKeyPlaceholder: 'AIza…',
  },
  openai_compatible: {
    label: 'OpenAI-compatible',
    description: 'Ollama, LM Studio, vLLM or any server with an OpenAI-style /models API.',
    apiKey: 'optional',
    baseUrl: 'required',
    defaultBaseUrl: null,
    baseUrlPlaceholder: 'http://localhost:11434/v1',
    apiKeyPlaceholder: 'Optional',
  },
  claude_agent_sdk: {
    label: 'Local Claude',
    description: 'Claude Code via the Claude Agent SDK, with its own agent loop and tools.',
    apiKey: 'optional',
    baseUrl: 'none',
    defaultBaseUrl: null,
    baseUrlPlaceholder: null,
    apiKeyPlaceholder: 'sk-ant-… (or use the server’s Claude login)',
  },
};

export const providerStatusSchema = z.enum(['untested', 'ok', 'error']);
export type ProviderStatus = z.infer<typeof providerStatusSchema>;

export const baseUrlSchema = z
  .string()
  .trim()
  .max(2048, 'Base URL is too long')
  .pipe(z.url({ protocol: /^https?$/, message: 'Enter an http:// or https:// URL' }))
  .transform((value) => value.replace(/\/+$/, ''));

export const apiKeySchema = z.string().trim().min(1, 'API key is required').max(4096);
export const modelIdSchema = z.string().trim().min(1).max(200);
const providerNameSchema = z.string().trim().min(1, 'Name is required').max(100);

export const createProviderRequestSchema = z.object({
  name: providerNameSchema,
  type: providerTypeSchema,
  baseUrl: baseUrlSchema.optional(),
  apiKey: apiKeySchema.optional(),
  defaultModel: modelIdSchema.optional(),
});
export type CreateProviderRequest = z.infer<typeof createProviderRequestSchema>;

export const updateProviderRequestSchema = z
  .object({
    name: providerNameSchema.optional(),
    baseUrl: baseUrlSchema.nullable().optional(),
    /** A string replaces the key, `null` removes it, omitted keeps it. */
    apiKey: apiKeySchema.nullable().optional(),
    defaultModel: modelIdSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateProviderRequest = z.infer<typeof updateProviderRequestSchema>;

export const providerDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  type: providerTypeSchema,
  baseUrl: z.string().nullable(),
  defaultModel: z.string().nullable(),
  apiKey: z.object({ last4: z.string() }).nullable(),
  status: providerStatusSchema,
  lastCheckedAt: isoTimestamp.nullable(),
  lastError: z.string().nullable(),
  models: z.array(z.string()),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type ProviderDto = z.infer<typeof providerDtoSchema>;

export const providerTestResultSchema = z.object({
  ok: z.boolean(),
  latencyMs: z.number().int().nonnegative(),
  models: z.array(z.string()),
  message: z.string().nullable(),
  provider: providerDtoSchema,
});
export type ProviderTestResult = z.infer<typeof providerTestResultSchema>;

/** Cross-field rules that depend on the provider type (shared by create and update). */
export function validateProviderShape(
  type: ProviderType,
  value: { baseUrl: string | null; apiKey: string | null },
  options: { claudeHostLogin: boolean },
): FieldError[] {
  const info = PROVIDER_CATALOG[type];
  const errors: FieldError[] = [];
  if (info.baseUrl === 'required' && !value.baseUrl) {
    errors.push({ path: 'baseUrl', message: 'A base URL is required for this provider' });
  }
  if (info.baseUrl === 'none' && value.baseUrl) {
    errors.push({ path: 'baseUrl', message: `${info.label} does not use a custom base URL` });
  }
  if (info.apiKey === 'required' && !value.apiKey) {
    errors.push({ path: 'apiKey', message: `An API key is required for ${info.label}` });
  }
  if (type === 'claude_agent_sdk' && !value.apiKey && !options.claudeHostLogin) {
    errors.push({
      path: 'apiKey',
      message: 'An API key is required: this server does not allow using its own Claude login',
    });
  }
  return errors;
}
